import { defineSchema } from "../../type-system/schema";

export const baseYotpoIntegrationSchema = defineSchema({
  fields: {
    websiteUrl: {
      dataType: "Maybe Text",
      documentation: "The website URL from the webhook event",
    },
    storeName: {
      dataType: "Maybe Text",
      documentation: "The store name from the webhook event",
    },
    publishedAt: {
      dataType: "Maybe Text",
      documentation: "ISO timestamp when the webhook event was published",
    },
    timestamp: {
      dataType: "Maybe Number",
      documentation: "Unix timestamp of the webhook event",
    },
    customerEmail: { dataType: "Email", documentation: "Customer email" },
    customerPhoneNumber: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer",
    },
    customerFirstName: {
      dataType: "Maybe Text",
      documentation: "The first name of the customer",
    },
    customerPointsBalance: {
      dataType: "Maybe Number",
      documentation: "Customer's current points balance",
    },
    customerPointsEarned: {
      dataType: "Maybe Number",
      documentation: "Total points earned by customer",
    },
    customerTotalSpendCents: {
      dataType: "Maybe Number",
      documentation: "Customer's total spend in cents",
    },
    customerTotalPurchases: {
      dataType: "Maybe Number",
      documentation: "Customer's total number of purchases",
    },
    customerThirdPartyId: {
      dataType: "Maybe Text",
      documentation: "Customer's third-party platform ID",
    },
    customerIsOptIn: {
      dataType: "Maybe Boolean",
      documentation: "Whether customer has opted into loyalty program",
    },
  },
});

export const exampleBaseYotpoIntegrationSchemaInstance = {
  email: "test@test.com",
  websiteUrl: "https://test.com",
  storeName: "Test Store",
  publishedAt: "2025-01-01",
  timestamp: 1_714_732_800,
  customerEmail: "test@test.com",
  customerPhoneNumber: "+1234567890",
  customerFirstName: "Test",
  customerPointsBalance: 100,
  customerPointsEarned: 100,
  customerTotalSpendCents: 100,
  customerTotalPurchases: 100,
  customerThirdPartyId: "test",
  customerIsOptIn: true,
};

export const yotpoLoyaltyExpirationReminderSchema = defineSchema({
  fields: {
    ...baseYotpoIntegrationSchema.fields,
    loyaltyNextPointsExpireOn: {
      dataType: "Maybe Text",
      documentation: "The date when the customer's next points will expire",
    },
    loyaltyNextPointsExpireAmount: {
      dataType: "Maybe Number",
      documentation: "The amount of points that will expire",
    },
    daysToPointExpiration: {
      dataType: "Maybe Number",
      documentation: "The number of days until the customer's points expire",
    },
  },
});

export const exampleYotpoLoyaltyExpirationReminderSchemaInstance = {
  ...exampleBaseYotpoIntegrationSchemaInstance,
  loyaltyNextPointsExpireOn: "2025-01-01",
  loyaltyNextPointsExpireAmount: 100,
  daysToPointExpiration: 10,
};

export const yotpoLoyaltyPointsReminderSchema = defineSchema({
  fields: {
    ...baseYotpoIntegrationSchema.fields,
    pointsNeeded: {
      dataType: "Maybe Number",
      documentation: "Number of points needed to reach next reward level",
    },
    redemptionOptionName: {
      dataType: "Maybe Text",
      documentation: "Name of the redemption option available",
    },
    redemptionOptionAmount: {
      dataType: "Maybe Number",
      documentation: "Points required for the redemption option",
    },
  },
});

export const exampleYotpoLoyaltyPointsReminderSchemaInstance = {
  ...exampleBaseYotpoIntegrationSchemaInstance,
  pointsNeeded: 50,
  redemptionOptionName: "$10 Off",
  redemptionOptionAmount: 500,
};

export const yotpoLoyaltyRedemptionReminderSchema = defineSchema({
  fields: {
    ...baseYotpoIntegrationSchema.fields,
    redemptionOptionName: {
      dataType: "Maybe Text",
      documentation: "Name of the available redemption option",
    },
    redemptionOptionDescription: {
      dataType: "Maybe Text",
      documentation: "Description of the redemption option",
    },
    redemptionOptionAmount: {
      dataType: "Maybe Number",
      documentation: "Points required for the redemption option",
    },
    redemptionOptionDiscountType: {
      dataType: "Maybe Text",
      documentation: "Type of discount (percentage, fixed, etc.)",
    },
  },
});

export const exampleYotpoLoyaltyRedemptionReminderSchemaInstance = {
  ...exampleBaseYotpoIntegrationSchemaInstance,
  redemptionOptionName: "$10 Off Next Purchase",
  redemptionOptionDescription: "Get $10 off your next purchase",
  redemptionOptionAmount: 500,
  redemptionOptionDiscountType: "fixed_amount",
};

export const yotpoLoyaltyRedemptionCreatedSchema = defineSchema({
  fields: {
    ...baseYotpoIntegrationSchema.fields,
    redemptionId: {
      dataType: "Maybe Number",
      documentation: "ID of the created redemption",
    },
    rewardText: {
      dataType: "Maybe Text",
      documentation: "Text description of the reward",
    },
    approved: {
      dataType: "Maybe Boolean",
      documentation: "Whether the redemption was approved",
    },
    redemptionOptionName: {
      dataType: "Maybe Text",
      documentation: "Name of the redeemed option",
    },
  },
});

export const exampleYotpoLoyaltyRedemptionCreatedSchemaInstance = {
  ...exampleBaseYotpoIntegrationSchemaInstance,
  redemptionId: 12_345,
  rewardText: "$10 off your next purchase",
  approved: true,
  redemptionOptionName: "$10 Off",
};

export const yotpoLoyaltyReferralCompletedSchema = defineSchema({
  fields: {
    ...baseYotpoIntegrationSchema.fields,
    referrerEmail: {
      dataType: "Email",
      documentation: "Email of the customer who made the referral",
    },
    referredEmail: {
      dataType: "Email",
      documentation: "Email of the customer who was referred",
    },
    referralCode: {
      dataType: "Maybe Text",
      documentation: "The referral code that was used",
    },
    rewardPoints: {
      dataType: "Maybe Number",
      documentation: "Points awarded for the referral",
    },
  },
});

export const exampleYotpoLoyaltyReferralCompletedSchemaInstance = {
  ...exampleBaseYotpoIntegrationSchemaInstance,
  referrerEmail: "referrer@test.com",
  referredEmail: "referred@test.com",
  referralCode: "REF123",
  rewardPoints: 100,
};

export const yotpoLoyaltyReferralSharedSchema = defineSchema({
  fields: {
    ...baseYotpoIntegrationSchema.fields,
    from: {
      dataType: "Email",
      documentation: "Email of the customer sharing the referral",
    },
    referralLink: {
      dataType: "Maybe Text",
      documentation: "The referral link that was shared",
    },
    referralCode: {
      dataType: "Maybe Text",
      documentation: "The referral code",
    },
    totalShares: {
      dataType: "Maybe Number",
      documentation: "Total number of times this referral has been shared",
    },
  },
});

export const exampleYotpoLoyaltyReferralSharedSchemaInstance = {
  ...exampleBaseYotpoIntegrationSchemaInstance,
  from: "sharer@test.com",
  referralLink: "https://store.com/referral/ABC123",
  referralCode: "ABC123",
  totalShares: 5,
};

export const yotpoLoyaltyCustomerBirthdaySchema = defineSchema({
  fields: {
    ...baseYotpoIntegrationSchema.fields,
    rewardPoints: {
      dataType: "Maybe Number",
      documentation: "Points awarded for birthday",
    },
    redemptionOptionName: {
      dataType: "Maybe Text",
      documentation: "Name of birthday reward option",
    },
  },
});

export const exampleYotpoLoyaltyCustomerBirthdaySchemaInstance = {
  ...exampleBaseYotpoIntegrationSchemaInstance,
  rewardPoints: 100,
  redemptionOptionName: "Birthday Special",
};

export const yotpoLoyaltyOptInSchema = defineSchema({
  fields: { ...baseYotpoIntegrationSchema.fields },
});

export const exampleYotpoLoyaltyOptInSchemaInstance = {
  ...exampleBaseYotpoIntegrationSchemaInstance,
};

export const yotpoLoyaltyTierEarnedSchema = defineSchema({
  fields: {
    ...baseYotpoIntegrationSchema.fields,
    newTierName: {
      dataType: "Maybe Text",
      documentation: "Name of the new tier earned",
    },
    newTierRank: {
      dataType: "Maybe Number",
      documentation: "Rank of the new tier",
    },
    oldTierName: {
      dataType: "Maybe Text",
      documentation: "Name of the previous tier",
    },
    pointsMultiplier: {
      dataType: "Maybe Number",
      documentation: "Points multiplier for the new tier",
    },
  },
});

export const exampleYotpoLoyaltyTierEarnedSchemaInstance = {
  ...exampleBaseYotpoIntegrationSchemaInstance,
  newTierName: "Gold",
  newTierRank: 2,
  oldTierName: "Silver",
  pointsMultiplier: 1.5,
};

export const yotpoLoyaltyTierLostSchema = defineSchema({
  fields: {
    ...baseYotpoIntegrationSchema.fields,
    oldTierName: {
      dataType: "Maybe Text",
      documentation: "Name of the tier that was lost",
    },
    oldTierRank: {
      dataType: "Maybe Number",
      documentation: "Rank of the tier that was lost",
    },
    newTierName: {
      dataType: "Maybe Text",
      documentation: "Name of the new tier",
    },
    newTierRank: {
      dataType: "Maybe Number",
      documentation: "Rank of the new tier",
    },
  },
});

export const exampleYotpoLoyaltyTierLostSchemaInstance = {
  ...exampleBaseYotpoIntegrationSchemaInstance,
  oldTierName: "Gold",
  oldTierRank: 2,
  newTierName: "Silver",
  newTierRank: 1,
};

export const yotpoLoyaltyPointsEarnedSchema = defineSchema({
  fields: {
    ...baseYotpoIntegrationSchema.fields,
    rewardPoints: {
      dataType: "Maybe Number",
      documentation: "Points earned in this event",
    },
    historyTitle: {
      dataType: "Maybe Text",
      documentation: "Description of how points were earned",
    },
    source: {
      dataType: "Maybe Text",
      documentation: "Source of the points (purchase, referral, etc.)",
    },
  },
});

export const exampleYotpoLoyaltyPointsEarnedSchemaInstance = {
  ...exampleBaseYotpoIntegrationSchemaInstance,
  rewardPoints: 50,
  historyTitle: "Points earned from purchase",
  source: "purchase",
};
